import { useState } from "react";
import { verifyContacts } from "@/api/quiz";
import {
  EMAIL_TAKEN_ERROR_MESSAGE,
  PHONE_TAKEN_ERROR_MESSAGE,
} from "@/constants";
import { InputField } from "@/components/common/input";
import ContactVerify from "@/pages/quiz/base/questions/contactField/components/contactVerify";
import { NoteIcon } from "@/components/common/icons/NoteIcon";
import { ProgressHeader } from "@/pages/quiz/base/components/progressHeader/progressHeader";
import { NUMBER_OF_ANSWERS } from "@/pages/quiz/base/questions/contactField/common";
import {
  phoneField,
  emailField,
  firstNameField,
  secondNameField,
  verificationCodeField,
} from "@/pages/quiz/base/questions/contactField/validationRules";
import { Button, ButtonType } from "@/components/common/button";
import { QuizQuestionProps } from "@/pages/quiz/context/types";
import { useManageInputFields } from "@/pages/quiz/hooks/useManageInputFields";

export type FormFieldName =
  | "firstName"
  | "secondName"
  | "phone"
  | "email"
  | "verificationCode";

type ContactFieldsValues = Record<FormFieldName, string>;

// Define the validation fields and their properties
const validationFields = {
  phone: phoneField,
  email: emailField,
  firstName: firstNameField,
  secondName: secondNameField,
  verificationCode: verificationCodeField,
};

const ContactsFieldsCPL = ({
  questionValue,
  setQuestionValue,
  setQuestionAnswer,
}: QuizQuestionProps<ContactFieldsValues>) => {
  const { registerField, error, setFieldError } = useManageInputFields(
    {
      setQuestionValue,
      questionValue,
    },
    validationFields // Pass only the relevant validation fields
  );

  const [isVerifyStep, setIsVerifyStep] = useState(false);

  const isAllAnswered =
    Object.values(questionValue).filter(Boolean).length === 5;

  const isNoErrors = Object.values(error).every(
    (errorMessage) => !errorMessage
  );

  const isFormAnswered = isAllAnswered && isNoErrors;

  const email = questionValue.email.toLowerCase();

  const isAllFilledBeforeVerify = () => {
    // exclude verify code from check list
    const isAllAnswered =
      Object.entries(questionValue)
        .filter(([key]) => key !== "verificationCode")
        .map(([_, value]) => value)
        .filter(Boolean).length ===
      NUMBER_OF_ANSWERS - 1;

    const isNoErrors = Object.entries(error)
      .filter(([key]) => key !== "verificationCode")
      .map(([_, value]) => value)
      .every((errorMessage) => !errorMessage);

    return isAllAnswered && isNoErrors;
  };

  const initialVerify = async () =>
    await verifyContacts({
      email,
      phone_number: questionValue["phone"],
    });

  const isUsedData = (failedField: any) => {
    if (failedField === "email") {
      setFieldError("email", EMAIL_TAKEN_ERROR_MESSAGE);
      return true;
    }
    if (failedField === "phone") {
      setFieldError("phone", PHONE_TAKEN_ERROR_MESSAGE);
      return true;
    }

    return false;
  };

  const handleNextClick = async () => {
    try {
      const failedField = await initialVerify();

      if (!isUsedData(failedField)) {
        setIsVerifyStep(true);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const onSubmit = async () => {
    if (isFormAnswered) {
      const serverErrors = await setQuestionAnswer({
        ...questionValue,
        email,
      }, true);

      console.log(serverErrors);
    }
  };

  const verifyStep = (
    <>
      <ContactVerify
        onBackPressed={() => setIsVerifyStep(false)}
        onResendCode={initialVerify}
        registerField={registerField}
        values={questionValue}
      />
      <Button
        type={ButtonType.CONTINUE}
        disabled={!isFormAnswered}
        onClick={onSubmit}
      />
    </>
  );

  const mainContent = (
    <>
      <div className=" flex flex-col-reverse items-center	 w-full justify-center gap-4 desktop:p-8">
        <div className="text-center max-w-[100%] w-[480px] flex-col flex gap-2">
          <h4 className="text-center uppercase section-title xs:text-2xl text-xl">
            You're Pre-Approved!
          </h4>
          <p className="section-title !text-[17px]">
            <NoteIcon />
            Due to incredibly high demand, please verify your phone number and
            one of our team members will contact you to check for availability.
          </p>

          <p className="text-base text-gray/80 mb-3">
            You can expect a call from us shortly.
          </p>

          <div className="flex direction-row gap-5 ">
            <InputField
              {...registerField("firstName")}
              label="First Name"
              required
            />
            <InputField {...registerField("secondName")} label="Last Name" />
          </div>
          <InputField {...registerField("phone")} label="Phone" type="phone" />
          <InputField {...registerField("email")} label="Email" />
        </div>
      </div>
      <div className="renderer-core-field-action is-visible mt-[28px]">
        <Button
          disabled={!isAllFilledBeforeVerify()}
          onClick={handleNextClick}
        />
      </div>
    </>
  );

  return (
    <>
      <ProgressHeader
        customOnBack={isVerifyStep ? () => setIsVerifyStep(false) : undefined}
      />
      {isVerifyStep ? verifyStep : mainContent}
    </>
  );
};

export { ContactsFieldsCPL };
